import {readMedicationDose,formatMedicationDose} from '../../shared/medicationDose';
import { hasInvalidMedicationName, isNumericMedicationName } from './medicationNameValidation';
import { readCanonicalMedicationRecord } from '../medications/canonicalMedicationRecord';

const DOSE_PATTERN = /\b(\d{1,3}(?:,\d{3})+(?:\.\d+)?|\d+(?:\.\d+)?)\s*(mg|mcg|g|ml|iu|units?|puffs?)\b/i;
const STATUS_PATTERN = /\b(active|paused|stopped|archived|discontinued|(?:as|if) needed)\b/i;
const TIME_PATTERN = /\b(\d{1,2})(?::(\d{2}))?\s*(am|pm)\b/i;
const TIME_PATTERN_GLOBAL = /\b(\d{1,2})(?::(\d{2}))?\s*(am|pm)\b/gi;
const FORMULATION_SUFFIXES = new Set(['er', 'xr', 'sr', 'cr', 'ir', 'dr', 'la', 'odt']);
const LEADING_STATUS_PATTERN = /^(active|paused|stopped|archived|discontinued|held|completed)\s*:?\s*/i;
const DOSE_EVENT_PATTERN = /\b(refused|missed|declined|not\s+taken|did(?:\s+not|n't)\s+take|would(?:\s+not|n't)\s+take)\b/i;

const normalizeWhitespace = (value = '') => String(value || '').replace(/\s+/g, ' ').trim();

const escapeRegExp = (value = '') => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const findMedicationNameBesideDose = ({ sourceText = '', dose = '', unit = '' } = {}) => {
  const normalizedDose = normalizeWhitespace(dose);
  const normalizedUnit = normalizeWhitespace(unit);
  if (!normalizedDose || !normalizedUnit) return '';

  const match = normalizeWhitespace(sourceText).match(new RegExp(
    `\\b([a-z][a-z'’-]*(?:\\s+(?:er|xr|sr|cr|ir|dr|la|odt))?)\\s+${escapeRegExp(normalizedDose)}\\s*${escapeRegExp(normalizedUnit)}\\b`,
    'i'
  ));
  return titleCaseMedicationName(match?.[1] || '');
};

const findExplicitMedicationMention = (sourceText = '') => {
  const match = normalizeWhitespace(sourceText).match(
    /\b([a-z][a-z'’-]*(?:\s+(?:er|xr|sr|cr|ir|dr|la|odt))?)\s+(\d+(?:\.\d+)?)\s*(mg|mcg|g|ml|iu|units?|puffs?)\b/i
  );
  return match ? {
    name: titleCaseMedicationName(match[1]),
    dose: match[2],
    unit: match[3],
  } : null;
};

const isMedicationNameBesideDose = ({ name = '', dose = '', unit = '', sourceText = '' } = {}) => (
  normalizeWhitespace(findMedicationNameBesideDose({ sourceText, dose, unit })).toLowerCase()
  === normalizeWhitespace(name).toLowerCase()
);

const MONTH_INDEX = Object.freeze({
  january: 0, february: 1, march: 2, april: 3, may: 4, june: 5,
  july: 6, august: 7, september: 8, october: 9, november: 10, december: 11,
});

const shiftDateKey = (dateKey = '', days = 0) => {
  const match = String(dateKey).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return '';
  return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]) + days, 12))
    .toISOString()
    .slice(0, 10);
};

const getSourceDateKey = (source = {}) => {
  const localDate = source.localDate
    || source.entryDate
    || source.occurredOnDate
    || source.metadata?.localDate
    || '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(localDate))) return String(localDate);
  const value = source.savedAt || source.capturedAt || source.createdAt || source.timestamp || '';
  const directMatch = String(value || '').match(/^(\d{4}-\d{2}-\d{2})/);
  if (directMatch) return directMatch[1];
  const date = value?.toDate?.() || (value ? new Date(value) : null);
  return date && !Number.isNaN(date.getTime()) ? date.toISOString().slice(0, 10) : '';
};

const getNextWeekdayDateKey = (dateKey = '', weekdayName = '') => {
  const weekdays = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const target = weekdays.indexOf(String(weekdayName || '').toLowerCase());
  const date = dateKey ? new Date(`${dateKey}T12:00:00Z`) : null;
  if (target < 0 || !date || Number.isNaN(date.getTime())) return '';
  const distance = ((target - date.getUTCDay() + 7) % 7) || 7;
  return shiftDateKey(dateKey, distance);
};

const parseTemporaryHold = (rawText = '', source = {}) => {
  const text = normalizeWhitespace(rawText);
  const match = text.match(/^hold\s+([a-z][a-z0-9'’-]*(?:\s+(?:er|xr|sr|cr|ir|dr|la|odt))?)\s+tomorrow(?:\s+morning)?(?:\s+because\s+of\s+(?:the\s+)?(.+?))?\.\s*restart\s+(sunday|monday|tuesday|wednesday|thursday|friday|saturday)\.?$/i);
  const sourceDate = getSourceDateKey(source);
  if (!match || !sourceDate) return null;
  const startDate = shiftDateKey(sourceDate, 1);
  const resumeDate = getNextWeekdayDateKey(sourceDate, match[3]);
  if (!startDate || !resumeDate || resumeDate <= startDate) return null;
  const name = titleCaseMedicationName(match[1]);
  return {
    id: buildSuggestionId(source.id, 0, name),
    name,
    aliases: [name],
    dose: '',
    unit: '',
    schedule: '',
    status: '',
    planAction: 'temporary_hold',
    temporaryHold: {
      startDate,
      endDate: shiftDateKey(resumeDate, -1),
      resumeDate,
      reason: normalizeWhitespace(match[2] || ''),
      requiresDateConfirmation: true,
    },
    sourceText: text,
    history: [],
    needsReview: true,
  };
};

const parseMedicationDiscontinuation = (rawText = '', source = {}) => {
  const text = normalizeWhitespace(rawText);
  const match = text.match(/^(?:(dr\.?\s+[a-z][a-z .'-]*?)\s+)?discontinued\s+([a-z][a-z0-9'’-]*(?:\s+(?:er|xr|sr|cr|ir|dr|la|odt))?)\s+today\.?(?:\s+(?:do not|don't)\s+give\s+any\s+more\s+doses\.?)?$/i);
  const effectiveDate = getSourceDateKey(source);
  if (!match || !effectiveDate) return null;
  const name = titleCaseMedicationName(match[2]);
  return {
    id: buildSuggestionId(source.id, 0, name),
    name,
    aliases: [name],
    dose: '',
    unit: '',
    schedule: '',
    status: 'Stopped',
    planAction: 'discontinue',
    discontinuation: {
      effectiveDate,
      provider: normalizeWhitespace(match[1] || ''),
      reason: 'Provider discontinued medication',
      requiresDateConfirmation: true,
    },
    sourceText: text,
    history: [],
    needsReview: true,
  };
};

const parseCompletedMedicationCourse = (rawText = '', source = {}) => {
  const text = normalizeWhitespace(rawText);
  if (!/\b(?:course\s+complete|finished\s+all\s+doses)\b/i.test(text)) return null;

  const suggestion = parseMedicationLine(text, 0, source);
  if (!suggestion?.name || !suggestion?.dose || !suggestion?.unit) return null;

  const sourceDate = getSourceDateKey(source);
  const finishedYesterday = /\b(?:finished\s+all\s+doses\s+)?yesterday\b/i.test(text);
  const effectiveDate = finishedYesterday && sourceDate ? shiftDateKey(sourceDate, -1) : '';

  return {
    ...suggestion,
    status: 'Completed',
    planAction: 'complete_course',
    completedCourse: {
      effectiveDate,
      effectiveDateText: finishedYesterday ? 'yesterday' : '',
      reason: 'Course complete',
      requiresDateConfirmation: !effectiveDate,
    },
    needsReview: true,
  };
};

const titleCaseMedicationName = (value = '') => (
  normalizeWhitespace(value)
    .split(/\s+/)
    .map((part) => (part ? `${part[0].toUpperCase()}${part.slice(1)}` : part))
    .join(' ')
);

const parseHistoricalMedicationSnapshot = (rawText = '', source = {}) => {
  const text = normalizeWhitespace(rawText);
  const contextMatch = text.match(/^(?:these\s+were\s+)?(?:[^:]*?\s+)?medications?\s+when\s+(?:she|he|they|[a-z][a-z'-]*)\s+was\s+discharged(?:\s+in\s+([a-z]+)\s+(\d{4}))?\s*:\s*(.+)$/i);
  if (!contextMatch) return null;

  const medicationParts = contextMatch[3].split(/\s*,\s*(?=[a-z][a-z0-9'’-]*(?:\s+(?:er|xr|sr|cr|ir|dr|la|odt))?\s+\d)/i);
  const medications = medicationParts
    .map((part, index) => parseMedicationLine(part.replace(/[.]$/, ''), index, source))
    .filter(Boolean)
    .map(({ id, name, dose, unit, schedule, status, form }) => ({ id, name, dose, unit, schedule, status, form }));
  if (medications.length < 2) return null;

  const monthName = normalizeWhitespace(contextMatch[1]).toLowerCase();
  const year = Number(contextMatch[2]);
  const hasMonthDate = Object.prototype.hasOwnProperty.call(MONTH_INDEX, monthName)
    && Number.isInteger(year);
  const date = hasMonthDate
    ? `${year}-${String(MONTH_INDEX[monthName] + 1).padStart(2, '0')}-01`
    : '';
  const dateText = hasMonthDate
    ? `${monthName.charAt(0).toUpperCase()}${monthName.slice(1)} ${year}`
    : '';

  return {
    id: buildSuggestionId(source.id, 0, 'medications-at-discharge'),
    name: 'Medications at discharge',
    aliases: [],
    dose: '',
    unit: '',
    schedule: '',
    status: 'Historical',
    planAction: 'historical_snapshot',
    historicalSnapshot: {
      dateText,
      date,
      dateApproximate: hasMonthDate,
      requiresDateConfirmation: true,
      medications,
    },
    sourceText: text,
    history: [],
    needsReview: true,
  };
};

const normalizeClockTime = (value = '') => {
  const match = normalizeWhitespace(value).match(TIME_PATTERN);
  if (!match) return '';

  const rawHour = Number(match[1]);
  const minute = Number(match[2] || 0);
  const period = match[3].toLowerCase();
  if (!Number.isInteger(rawHour) || !Number.isInteger(minute)) return '';

  let hour = rawHour;
  if (period === 'pm' && hour < 12) hour += 12;
  if (period === 'am' && hour === 12) hour = 0;

  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
};

const normalizeClockTimes = (value = '') => {
  const text = normalizeWhitespace(value);
  if (!text) return [];

  return [...text.matchAll(TIME_PATTERN_GLOBAL)]
    .map((match) => normalizeClockTime(match[0]))
    .filter(Boolean);
};

const buildSuggestionId = (sourceId = '', index = 0, name = '') => {
  const slug = normalizeWhitespace(name)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);

  return [sourceId || 'source', 'med', index, slug || 'suggestion'].join('-');
};

const formatDoseWithUnit = (dose='',unit='') => formatMedicationDose({dose,unit});

const getTextAfterFirstSentence = (value = '') => {
  const text = normalizeWhitespace(value);
  const sentenceEndIndex = text.search(/[.?!]/);
  if (sentenceEndIndex < 0) return '';
  return normalizeWhitespace(text.slice(sentenceEndIndex + 1));
};

const buildMedicationLineNotes = ({ purpose = '', trailingNotes = '', planDetails = {} } = {}) => {
  const notes = [purpose, trailingNotes];
  const startDateText = normalizeWhitespace(planDetails.startDateText || '');
  const previousDose = formatDoseWithUnit(planDetails.previousDose, planDetails.previousUnit);
  const newDose = formatDoseWithUnit(planDetails.newDose, planDetails.newUnit);
  const changeDateText = normalizeWhitespace(planDetails.changeDateText || '');
  const changeReason = normalizeWhitespace(planDetails.changeReason || '');

  if (!trailingNotes && startDateText) {
    notes.push(`Started ${startDateText}.`);
  }

  if (!trailingNotes && previousDose && newDose) {
    notes.push(normalizeWhitespace([
      `Increased from ${previousDose} to ${newDose}`,
      changeDateText ? `in ${changeDateText}` : '',
      changeReason ? `after ${changeReason}` : '',
    ].filter(Boolean).join(' ')) + '.');
  }

  return normalizeWhitespace(notes.join(' '));
};

const buildMedicationBlockNotes = (block = {}) => (
  normalizeWhitespace([
    block.prescribingDoctor ? `Prescribing doctor: ${block.prescribingDoctor}.` : '',
    block.notes || block.purpose || '',
  ].filter(Boolean).join(' '))
);

const stripListMarker = (value = '') => (
  normalizeWhitespace(value).replace(/^(?:[-*•·]|\d+[.)])\s+/, '').trim()
);

const NARRATIVE_MEDICATION_CONTEXT_PREFIX = /^(?:morning|afternoon|evening|bedtime)\s+(?:meds?|medications?)\s*:\s*/i;
const NARRATIVE_DETAIL_PREFIX = /^(?:started|increased|decreased|changed|originally|previously|monitor|watch|contact)\b/i;

const startsNarrativeMedication = (sentence = '') => (
  DOSE_PATTERN.test(sentence) && !NARRATIVE_DETAIL_PREFIX.test(normalizeWhitespace(sentence))
);

const splitNarrativeMedicationLine = (value = '') => {
  const text = normalizeWhitespace(value);
  const sentences = text.split(/(?<=[.!?])\s+(?=[A-Z])/).filter(Boolean);
  if (sentences.filter(startsNarrativeMedication).length < 2) return [text];

  return sentences.reduce((entries, sentence) => {
    if (startsNarrativeMedication(sentence)) {
      entries.push(normalizeWhitespace(sentence.replace(NARRATIVE_MEDICATION_CONTEXT_PREFIX, '')));
    } else if (entries.length) {
      entries[entries.length - 1] = normalizeWhitespace(`${entries[entries.length - 1]} ${sentence}`);
    }
    return entries;
  }, []);
};

const getNonEmptyLines = (rawText = '') => (
  String(rawText || '')
    .split(/\r?\n/)
    .map((line) => normalizeWhitespace(line))
    .filter(Boolean)
);

const getSectionValue = (lines = [], label = '') => {
  const labelIndex = lines.findIndex((line) => normalizeWhitespace(line).toLowerCase() === label);
  if (labelIndex < 0) return '';

  const values = [];
  for (let index = labelIndex + 1; index < lines.length; index += 1) {
    const line = lines[index];
    const lowerLine = line.toLowerCase();
    if (['purpose', 'monitor', 'contact provider if'].includes(lowerLine)) break;
    values.push(stripListMarker(line));
  }

  return normalizeWhitespace(values.join(', '));
};

const parseMedicationPlanDocument = (rawText = '', source = {}) => {
  const lines = getNonEmptyLines(rawText);
  const planIndex = lines.findIndex((line) => /^medication plan$/i.test(line));
  if (planIndex < 0) return null;

  const name = normalizeWhitespace(lines[planIndex + 1] || '');
  const doseScheduleText = normalizeWhitespace(lines[planIndex + 2] || '');
  const doseMatch = doseScheduleText.match(DOSE_PATTERN);
  if (!name || !doseMatch) return null;

  const schedule = normalizeWhitespace(doseScheduleText.replace(doseMatch[0], ''));
  const purpose = getSectionValue(lines, 'purpose');
  const monitor = getSectionValue(lines, 'monitor');
  const contactProvider = getSectionValue(lines, 'contact provider if');
  const medicationName = titleCaseMedicationName(name);
  const notes = normalizeWhitespace([
    purpose,
    monitor ? `Monitor: ${monitor}.` : '',
    contactProvider ? `Contact provider if: ${contactProvider}.` : '',
  ].filter(Boolean).join(' '));

  return {
    id: buildSuggestionId(source.id, 0, medicationName),
    name: medicationName,
    genericName: medicationName,
    brandName: '',
    aliases: [medicationName],
    purpose,
    notes,
    dose: doseMatch[1] || '',
    unit: doseMatch[2] || '',
    schedule,
    times: normalizeClockTimes(schedule),
    time: '',
    status: '',
    form: '',
    prescribingDoctor: '',
    sourceText: normalizeWhitespace(rawText),
    history: [],
    needsReview: true,
  };
};


const getMedicationLineEntries = (rawText = '') => {
  const entries = [];
  let currentEntry = '';

  const finishEntry = () => {
    const cleanEntry = normalizeWhitespace(currentEntry);
    if (cleanEntry) entries.push(cleanEntry);
    currentEntry = '';
  };

  String(rawText || '').split(/\r?\n/).flatMap(splitNarrativeMedicationLine).forEach((line) => {
    const text = normalizeWhitespace(line);
    if (!text) return;

    const strippedText = stripListMarker(text);
    const hasListMarker = strippedText !== text;
    const startsMedicationEntry = hasListMarker || DOSE_PATTERN.test(strippedText);

    if (startsMedicationEntry) {
      finishEntry();
      currentEntry = strippedText;
      return;
    }

    if (currentEntry) {
      currentEntry = `${currentEntry} ${strippedText}`;
      return;
    }

    entries.push(strippedText);
  });

  finishEntry();
  return entries;
};

const getMedicationNameParts = (value = '') => {
  const text = normalizeWhitespace(value);
  const aliases = [];
  const parentheticalMatch = text.match(/^(.+?)\s*\(([^)]+)\)\s*$/);

  if (!parentheticalMatch) {
    return {
      name: titleCaseMedicationName(text),
      genericName: '',
      brandName: '',
      aliases,
    };
  }

  const genericName = titleCaseMedicationName(parentheticalMatch[1]);
  const brandName = titleCaseMedicationName(parentheticalMatch[2]);
  if (genericName) aliases.push(genericName);
  if (brandName) aliases.push(brandName);

  return {
    name: `${genericName} (${brandName})`,
    genericName,
    brandName,
    aliases,
  };
};

const extractNameAndPurpose = (beforeDose = '') => {
  const text = stripListMarker(beforeDose);
  const parentheticalPrefix = text.match(/^(.+?\([^)]+\))(?:\s+(.+))?$/);
  if (parentheticalPrefix) {
    return {
      ...getMedicationNameParts(parentheticalPrefix[1]),
      purpose: normalizeWhitespace(parentheticalPrefix[2] || ''),
    };
  }

  const beforeParts = text.split(/\s+/).filter(Boolean);
  const namePartCount = /^vitamin$/i.test(beforeParts[0]) && /^[a-z0-9]+$/i.test(beforeParts[1] || '')
    ? 2
    : FORMULATION_SUFFIXES.has(String(beforeParts[1] || '').toLowerCase())
      ? 2
    : 1;
  const name = titleCaseMedicationName(beforeParts.slice(0, namePartCount).join(' '));

  return {
    ...getMedicationNameParts(name),
    purpose: normalizeWhitespace(beforeParts.slice(namePartCount).join(' ')),
  };
};

const getFirstSentence = (value = '') => normalizeWhitespace(String(value || '').split(/[.?!]/)[0] || '');

const normalizeScheduleText = (value = '') => (
  getFirstSentence(value)
    .replace(/\bstarted\b.*$/i, '')
    .replace(/\bincreased\b.*$/i, '')
    .trim()
);

const extractOriginalDoseDetails = (text = '') => {
  const originalDoseMatch = normalizeWhitespace(text).match(/\boriginally\s+started\s+at\s+(\d+(?:\.\d+)?)\s*(mg|mcg|g|ml|iu|units?)?/i);
  if (!originalDoseMatch) return {};

  return {
    originalDose: originalDoseMatch[1] || '',
    originalUnit: originalDoseMatch[2] || '',
  };
};

const extractDoseChangeDetails = (text = '', currentDose = '', currentUnit = '') => {
  const sourceText = normalizeWhitespace(text);
  const changeMatch = sourceText.match(/\bincreased\s+from\s+(\d+(?:\.\d+)?)\s*(mg|mcg|g|ml|iu|units?)?\s+to\s+(\d+(?:\.\d+)?)\s*(mg|mcg|g|ml|iu|units?)?/i);
  const originalDoseDetails = extractOriginalDoseDetails(sourceText);
  if (!changeMatch && !(originalDoseDetails.originalDose && currentDose && /\bincreased\b/i.test(sourceText))) {
    return {};
  }

  const changeDateMatch = sourceText.match(/\bincreased\b.+?\bon\s+([A-Za-z]+\s+\d{1,2})\b/i)
    || sourceText.match(/\bincreased\b.+?\bin\s+([A-Za-z]+)\b/i);
  const reasonMatch = sourceText.match(/\b(?:after|because)\s+(.+?)(?:[.?!]|$)/i);
  const previousDose = changeMatch?.[1] || originalDoseDetails.originalDose || '';
  const previousUnit = changeMatch?.[2] || changeMatch?.[4] || originalDoseDetails.originalUnit || currentUnit || '';
  const newDose = changeMatch?.[3] || currentDose || '';
  const newUnit = changeMatch?.[4] || changeMatch?.[2] || currentUnit || previousUnit || '';

  return {
    previousDose,
    previousUnit,
    newDose,
    newUnit,
    changeDateText: normalizeWhitespace(changeDateMatch?.[1] || ''),
    changeDateApproximate: Boolean(changeDateMatch?.[1]),
    changeReason: normalizeWhitespace(reasonMatch?.[1] || ''),
  };
};

const extractNarrativeDoseChange = (text = '') => {
  const match = normalizeWhitespace(text).match(
    /\b(increased|decreased|changed)\s+(?:(?:her|his|their|the)\s+)?([a-z][a-z0-9'’-]*(?:\s+(?:er|xr|sr|cr|ir|dr|la|odt))?)\s+from\s+(\d+(?:\.\d+)?)\s*(mg|mcg|g|ml|iu|units?)?\s+to\s+(\d+(?:\.\d+)?)\s*(mg|mcg|g|ml|iu|units?)?([^.!?]*)/i
  );
  if (!match) return null;

  return {
    name: titleCaseMedicationName(match[2]),
    previousDose: match[3] || '',
    previousUnit: match[4] || match[6] || '',
    newDose: match[5] || '',
    newUnit: match[6] || match[4] || '',
    schedule: normalizeWhitespace(match[7] || ''),
  };
};

const extractStartDetails = (text = '') => {
  const startMatch = normalizeWhitespace(text).match(/\bstarted\s+(.+?)(?:[.?!]|$)/i);
  if (!startMatch) return {};
  const startText = normalizeWhitespace(startMatch[1] || '');
  if (/^at\s+\d+(?:\.\d+)?\s*(?:mg|mcg|g|ml|iu|units?)?$/i.test(startText)) {
    return {};
  }

  return {
    startDateText: startText,
    startDateApproximate: true,
  };
};

const buildSuggestionHistory = ({
  dose = '',
  unit = '',
  schedule = '',
  sourceText = '',
  planDetails = {},
} = {}) => {
  const history = [];
  const startDateText = normalizeWhitespace(planDetails.startDateText || '');
  const previousDose = normalizeWhitespace(planDetails.previousDose || '');
  const previousUnit = normalizeWhitespace(planDetails.previousUnit || unit || '');
  const newDose = normalizeWhitespace(planDetails.newDose || dose || '');
  const newUnit = normalizeWhitespace(planDetails.newUnit || unit || '');
  const changeDateText = normalizeWhitespace(planDetails.changeDateText || '');
  const changeReason = normalizeWhitespace(planDetails.changeReason || '');
  const reviewReasons = ['exact_date_needed'];

  if (startDateText && previousDose) {
    history.push({
      type: 'started',
      approximateDateText: startDateText,
      dateApproximate: true,
      dose: previousDose,
      unit: previousUnit,
      schedule,
      reason: '',
      sourceText,
      needsReview: true,
      reviewReasons,
    });
  }

  if (previousDose && newDose) {
    const changeNeedsExactDate = !changeDateText || Boolean(planDetails.changeDateApproximate);

    history.push({
      type: 'dose_change',
      approximateDateText: changeDateText,
      dateApproximate: Boolean(changeDateText),
      previousDose,
      previousUnit,
      newDose,
      newUnit,
      schedule,
      reason: changeReason,
      sourceText,
      needsReview: changeNeedsExactDate || !changeReason,
      reviewReasons: [
        ...(changeNeedsExactDate ? reviewReasons : []),
        ...(!changeReason ? ['reason_needed'] : []),
      ],
    });
  }

  return history;
};

const parseMedicationLine = (line = '', index = 0, source = {}) => {
  const text = normalizeWhitespace(line);
  if (!text || /^medication\s+purpose\s+dose\s+schedule\s+status$/i.test(text)) {
    return null;
  }

  const leadingStatusMatch = text.match(LEADING_STATUS_PATTERN);
  const medicationText = leadingStatusMatch
    ? normalizeWhitespace(text.slice(leadingStatusMatch[0].length))
    : text;
  const narrativeDoseChange = extractNarrativeDoseChange(medicationText);
  const doseMatch = medicationText.match(DOSE_PATTERN);
  if (!doseMatch || doseMatch.index == null) return null;

  const beforeDose = normalizeWhitespace(medicationText.slice(0, doseMatch.index));
  const afterDose = normalizeWhitespace(medicationText.slice(doseMatch.index + doseMatch[0].length));
  const nameParts = narrativeDoseChange
    ? { ...getMedicationNameParts(narrativeDoseChange.name), purpose: '' }
    : extractNameAndPurpose(beforeDose);
  if (hasInvalidMedicationName(nameParts.name)) return null;

  const statusMatch = afterDose.match(STATUS_PATTERN);
  const status = leadingStatusMatch
    ? titleCaseMedicationName(leadingStatusMatch[1])
    : statusMatch ? (/^(?:as|if) needed$/i.test(statusMatch[0]) ? 'As Needed' : titleCaseMedicationName(statusMatch[0])) : '';
  const rawSchedule = normalizeWhitespace(statusMatch && statusMatch.index != null
    ? afterDose.slice(0, statusMatch.index)
    : afterDose);
  const schedule = narrativeDoseChange
    ? normalizeScheduleText(narrativeDoseChange.schedule)
    : normalizeScheduleText(rawSchedule);
  if (DOSE_EVENT_PATTERN.test(schedule)) return null;
  const time = normalizeClockTime(schedule);
  const times = normalizeClockTimes(schedule);
  const initialDose = doseMatch[1];
  const initialUnit = doseMatch[2];
  const planDetails = {
    ...extractStartDetails(text),
    ...extractDoseChangeDetails(text, initialDose, initialUnit),
    ...(narrativeDoseChange || {}),
  };
  const effectiveDose = planDetails.newDose || initialDose;
  const effectiveUnit = planDetails.newUnit || initialUnit;
  const notes = buildMedicationLineNotes({
    purpose: nameParts.purpose,
    trailingNotes: getTextAfterFirstSentence(text),
    planDetails: {
      ...planDetails,
      newDose: planDetails.newDose || effectiveDose,
      newUnit: planDetails.newUnit || effectiveUnit,
    },
  });

  return {
    id: buildSuggestionId(source.id, index, nameParts.name),
    name: nameParts.name,
    genericName: nameParts.genericName,
    brandName: nameParts.brandName,
    aliases: nameParts.aliases,
    purpose: nameParts.purpose,
    notes,
    dose: effectiveDose,
    unit: effectiveUnit,
    schedule,
    time,
    times,
    status,
    previousDose: planDetails.previousDose || '',
    previousUnit: planDetails.previousUnit || '',
    changeDateText: planDetails.changeDateText || '',
    changeDateApproximate: Boolean(planDetails.changeDateApproximate),
    changeReason: planDetails.changeReason || '',
    startDateText: planDetails.startDateText || '',
    startDateApproximate: Boolean(planDetails.startDateApproximate),
    history: buildSuggestionHistory({
      dose: effectiveDose,
      unit: effectiveUnit,
      schedule,
      sourceText: text,
      planDetails,
    }),
    sourceText: text,
    needsReview: !effectiveDose
      || Boolean(planDetails.previousDose)
      || Boolean(planDetails.changeDateApproximate || planDetails.startDateApproximate),
  };
};

const normalizeMedicationForm = (value = '') => {
  const text = normalizeWhitespace(value).toLowerCase();
  if (!text) return '';
  if (text.includes('tablet')) return 'tablet';
  if (text.includes('capsule')) return 'capsule';
  if (text.includes('liquid')) return 'liquid';
  if (text.includes('drop')) return 'drops';
  if (text.includes('cream')) return 'cream';
  if (text.includes('inhaler')) return 'inhaler';
  return text.replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
};

const parseMedicationBlock = (block = {}, index = 0, source = {}) => {
  const nameParts = getMedicationNameParts(block.name || '');
  if (hasInvalidMedicationName(nameParts.name)) return null;

  const doseText = normalizeWhitespace(block.dose || block.sourceText || '');
  const doseMatch = doseText.match(DOSE_PATTERN);
  const rawSchedule = normalizeWhitespace(block.schedule || '');
  const scheduleStatusMatch = rawSchedule.match(/\bas needed\b/i);
  const schedule = scheduleStatusMatch
    ? normalizeWhitespace(rawSchedule.replace(scheduleStatusMatch[0], ''))
    : rawSchedule;
  const times = normalizeClockTimes(schedule);
  const status = scheduleStatusMatch
    ? titleCaseMedicationName(scheduleStatusMatch[0])
    : normalizeWhitespace(block.status || '');
  const notes = buildMedicationBlockNotes(block);

  return {
    id: buildSuggestionId(source.id, index, nameParts.name),
    name: nameParts.name,
    genericName: nameParts.genericName,
    brandName: nameParts.brandName,
    aliases: nameParts.aliases,
    purpose: normalizeWhitespace(block.purpose || ''),
    notes,
    dose: doseMatch?.[1] || '',
    unit: doseMatch?.[2] || '',
    schedule,
    times,
    time: times[0] || '',
    status,
    form: normalizeMedicationForm(block.form || ''),
    prescribingDoctor: normalizeWhitespace(block.prescribingDoctor || ''),
    sourceText: normalizeWhitespace(block.sourceText || ''),
    history: [],
    needsReview: !doseMatch,
  };
};

const applyMedicationBlockField = (block, label = '', value = '') => {
  const cleanLabel = normalizeWhitespace(label).toLowerCase();
  const cleanValue = normalizeWhitespace(value);
  if (!cleanValue) return;

  if (cleanLabel === 'medication') {
    block.name = cleanValue;
    return;
  }
  if (cleanLabel === 'prescribing doctor' || cleanLabel === 'doctor' || cleanLabel === 'prescriber') {
    block.prescribingDoctor = cleanValue;
    return;
  }
  if (cleanLabel === 'time to give' || cleanLabel === 'time' || cleanLabel === 'schedule') {
    block.schedule = cleanValue;
    return;
  }
  if (cleanLabel === 'form') {
    block.form = cleanValue;
    return;
  }
  if (cleanLabel === 'details' || cleanLabel === 'purpose' || cleanLabel === 'notes') {
    block.purpose = cleanValue;
    block.notes = cleanValue;
    return;
  }
  if (cleanLabel === 'dose') {
    block.dose = cleanValue;
    return;
  }
  if (cleanLabel === 'status') {
    block.status = cleanValue;
  }
};

const parseFormattedTableMedicationRow = (text = '', index = 0, source = {}) => {
  const rowMatch = normalizeWhitespace(text).match(/^row\s+\d+:\s*(.+)$/i);
  if (!rowMatch) return null;

  const block = { sourceText: text };
  rowMatch[1].split(/\s*;\s*/).forEach((cell) => {
    const labelMatch = normalizeWhitespace(cell).match(/^([^:]+):\s*(.+)$/);
    if (!labelMatch) return;

    applyMedicationBlockField(block, labelMatch[1], labelMatch[2]);
  });

  return parseMedicationBlock(block, index, source);
};

const normalizeTableColumnLabel = (value = '') => {
  const label = normalizeWhitespace(value).toLowerCase().replace(/[^a-z0-9#]+/g, ' ').trim();
  if (['#', 'no', 'number', 'row'].includes(label)) return 'rowNumber';
  if (['medication', 'medication name', 'medicine', 'drug'].includes(label)) return 'medication';
  if (['dose', 'dosage', 'strength'].includes(label)) return 'dose';
  if (['instructions', 'schedule', 'instructions schedule', 'directions', 'sig'].includes(label)) return 'schedule';
  if (label === 'status') return 'status';
  return '';
};

const splitPipeTableRow = (line = '') => (
  String(line || '').trim().replace(/^\|+|\|+$/g, '').split('|').map(normalizeWhitespace)
);

const getPipeTableSuggestionsFromText = (rawText = '', source = {}) => {
  const lines = String(rawText || '').split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  const headerIndex = lines.findIndex((line) => {
    if (!line.includes('|')) return false;
    const labels = splitPipeTableRow(line).map(normalizeTableColumnLabel);
    return labels.includes('medication') && labels.includes('dose');
  });
  if (headerIndex < 0) return [];

  const columns = splitPipeTableRow(lines[headerIndex]).map(normalizeTableColumnLabel);
  const suggestions = [];
  for (let lineIndex = headerIndex + 1; lineIndex < lines.length; lineIndex += 1) {
    const line = lines[lineIndex];
    if (!line.includes('|') || /^\s*\|?\s*:?-{2,}/.test(line)) continue;
    const values = splitPipeTableRow(line);
    if (values.length < columns.length) continue;
    const block = { sourceText: normalizeWhitespace(line) };
    columns.forEach((column, columnIndex) => {
      if (!column || column === 'rowNumber') return;
      applyMedicationBlockField(block, column, values[columnIndex]);
    });
    const suggestion = parseMedicationBlock(block, suggestions.length, source);
    if (suggestion) suggestions.push(suggestion);
  }
  return suggestions;
};

const getMedicationBlockSuggestionsFromText = (rawText = '', source = {}) => {
  const suggestions = [];
  let currentBlock = null;
  let currentIndex = 0;

  const finishBlock = () => {
    if (!currentBlock) return;

    const suggestion = parseMedicationBlock(currentBlock, currentIndex, source);
    if (suggestion) {
      suggestions.push(suggestion);
      currentIndex += 1;
    }
    currentBlock = null;
  };

  String(rawText || '').split(/\r?\n/).forEach((line) => {
    const text = normalizeWhitespace(line);
    if (!text) return;

    const tableRowSuggestion = parseFormattedTableMedicationRow(text, currentIndex, source);
    if (tableRowSuggestion) {
      finishBlock();
      suggestions.push(tableRowSuggestion);
      currentIndex += 1;
      return;
    }

    const labelMatch = text.match(/^([^:]+):\s*(.+)$/);
    if (!labelMatch) {
      if (currentBlock) {
        currentBlock.sourceText = [currentBlock.sourceText, text].filter(Boolean).join('\n');
      }
      return;
    }

    const [, label, value] = labelMatch;
    if (/^medication$/i.test(label)) {
      finishBlock();
      currentBlock = {
        name: value,
        sourceText: text,
      };
      return;
    }

    if (!currentBlock) return;

    currentBlock.sourceText = [currentBlock.sourceText, text].filter(Boolean).join('\n');
    applyMedicationBlockField(currentBlock, label, value);
  });

  finishBlock();

  return suggestions;
};

const normalizeBackendSuggestion = (suggestion = {}, index = 0, source = {}) => {
  const backendName = titleCaseMedicationName(suggestion.name || '');
  const misplacedName = titleCaseMedicationName(suggestion.purpose || '');
  const sourceText = [source.cleanedText, source.rawText, source.originalText, source.sourceText]
    .find((value) => normalizeWhitespace(value)) || '';
  const recoveredName = isNumericMedicationName(backendName) && misplacedName
    && String(sourceText).split(/\r?\n/).some((line) => {
      const normalizedLine = normalizeWhitespace(line);
      const prefix = `${backendName} ${misplacedName} `;
      return normalizedLine.toLowerCase().startsWith(prefix.toLowerCase())
        && DOSE_PATTERN.test(normalizedLine.slice(prefix.length));
    })
    ? misplacedName
    : '';
  const name = recoveredName || backendName;
  const quantity=readMedicationDose({dose:suggestion.dose,unit:suggestion.unit,doseText:suggestion.doseText,medicationName:name,sourceText:suggestion.sourceText || sourceText});
  const quantityIncomplete=Boolean((suggestion.dose || suggestion.doseText) && quantity.needsClarification);
  const id = suggestion.id || buildSuggestionId(source.id, index, name);
  const purpose = recoveredName ? '' : normalizeWhitespace(suggestion.purpose || '');
  const notes = recoveredName && normalizeWhitespace(suggestion.notes) === normalizeWhitespace(suggestion.purpose)
    ? ''
    : normalizeWhitespace(suggestion.notes || purpose);
  const canonicalMedicationRecord = suggestion.canonicalMedicationRecord?.schemaVersion === 1
    ? readCanonicalMedicationRecord(suggestion)
    : null;
  const completedCourse = suggestion.completedCourse && typeof suggestion.completedCourse === 'object'
    ? { ...suggestion.completedCourse }
    : canonicalMedicationRecord?.action === 'complete_course'
      ? {
          effectiveDate: canonicalMedicationRecord.effective?.date || '',
          effectiveDateText: canonicalMedicationRecord.effective?.dateText || '',
          reason: canonicalMedicationRecord.reason || 'Course complete',
          requiresDateConfirmation: !canonicalMedicationRecord.effective?.date,
        }
      : null;
  const historicalSnapshot = suggestion.historicalSnapshot && typeof suggestion.historicalSnapshot === 'object'
    ? { ...suggestion.historicalSnapshot }
    : canonicalMedicationRecord?.action === 'historical_snapshot'
      ? {
          ...(canonicalMedicationRecord.actionDetails || {}),
          date: canonicalMedicationRecord.actionDetails?.date || canonicalMedicationRecord.effective?.date || '',
          dateText: canonicalMedicationRecord.actionDetails?.dateText || canonicalMedicationRecord.effective?.dateText || '',
          dateApproximate: canonicalMedicationRecord.actionDetails?.dateApproximate
            ?? canonicalMedicationRecord.effective?.certainty === 'approximate',
          medications: Array.isArray(canonicalMedicationRecord.actionDetails?.medications)
            ? canonicalMedicationRecord.actionDetails.medications.map((medication) => ({ ...medication }))
            : [],
        }
      : null;

  return {
    id,
    name,
    genericName: titleCaseMedicationName(suggestion.genericName || ''),
    brandName: titleCaseMedicationName(suggestion.brandName || ''),
    aliases: Array.isArray(suggestion.aliases)
      ? suggestion.aliases.map((alias) => titleCaseMedicationName(alias)).filter(Boolean)
      : [],
    purpose,
    notes,
    dose:suggestion.canonicalMedicationRecord?.actionDetails?.issue === 'conflicting_plans' ? '' : quantity.amount,unit:quantity.unit,doseText:suggestion.canonicalMedicationRecord?.actionDetails?.issue === 'conflicting_plans' ? '' : quantity.doseText,
    ...(quantityIncomplete?{clarification:{issue:'missing_dose_unit',reason:quantity.reason},reviewReasons:['dose_unit_needed']}:{}),
    schedule: normalizeWhitespace(suggestion.schedule || suggestion.sourceScheduleText || ''),
    times: Array.isArray(suggestion.times)
      ? suggestion.times.map((time) => normalizeWhitespace(time)).filter(Boolean)
      : [],
    time: normalizeWhitespace(suggestion.time || ''),
    status: normalizeWhitespace(suggestion.status || suggestion.sourceStatus || ''),
    form: normalizeMedicationForm(suggestion.form || ''),
    prescribingDoctor: normalizeWhitespace(suggestion.prescribingDoctor || ''),
    previousDose: normalizeWhitespace(suggestion.previousDose || ''),
    previousUnit: normalizeWhitespace(suggestion.previousUnit || ''),
    changeDateText: normalizeWhitespace(suggestion.changeDateText || ''),
    changeDateApproximate: Boolean(suggestion.changeDateApproximate),
    changeReason: normalizeWhitespace(suggestion.changeReason || ''),
    startDateText: normalizeWhitespace(suggestion.startDateText || ''),
    startDateApproximate: Boolean(suggestion.startDateApproximate),
    history: Array.isArray(suggestion.history) ? suggestion.history : [],
    sourceText: normalizeWhitespace(suggestion.sourceText || ''),
    needsReview: suggestion.needsReview !== false,
    planAction: quantityIncomplete ? 'needs_clarification' : normalizeWhitespace(suggestion.planAction || canonicalMedicationRecord?.action || ''),
    temporaryHold: suggestion.temporaryHold && typeof suggestion.temporaryHold === 'object'
      ? { ...suggestion.temporaryHold }
      : null,
    discontinuation: suggestion.discontinuation && typeof suggestion.discontinuation === 'object'
      ? { ...suggestion.discontinuation }
      : null,
    completedCourse,
    historicalSnapshot,
    canonicalMedicationRecord,
  };
};

const getBackendMedicationSuggestions = (source = {}) => {
  const suggestions = Array.isArray(source.suggestedMedicationUpdates)
    ? source.suggestedMedicationUpdates
    : [];

  return suggestions
    .map((suggestion, index) => normalizeBackendSuggestion(suggestion, index, source))
    .filter((suggestion) => suggestion.name || suggestion.planAction === 'needs_clarification');
};

const getStructuredMedicationSuggestions = (source = {}) => {
  const suggestionsByPlan = new Map();
  const items = Array.isArray(source.suggestedCareKnowledgeItems)
    ? source.suggestedCareKnowledgeItems
    : [];

  items.forEach((item, index) => {
    if (normalizeWhitespace(item?.knowledgeType).toLowerCase() !== 'medication') return;

    const metadata = item?.metadata && typeof item.metadata === 'object' ? item.metadata : {};
    const sourceText = normalizeWhitespace(item.sourceText || item.content);
    const explicitMention = findExplicitMedicationMention(sourceText);
    const doseMatch = normalizeWhitespace(metadata.dose || item.dose).match(DOSE_PATTERN)
      || readMedicationDose({dose:metadata.dose||item.dose,unit:metadata.unit||item.unit}).doseText.match(DOSE_PATTERN)
      || (explicitMention ? [explicitMention.dose + explicitMention.unit, explicitMention.dose, explicitMention.unit] : null);
    const metadataMedicationName = normalizeWhitespace(metadata.medicationName);
    const hasSupportedMetadataName = metadataMedicationName && doseMatch && isMedicationNameBesideDose({
      name: metadataMedicationName,
      dose: doseMatch[1],
      unit: doseMatch[2],
      sourceText,
    });
    const name = titleCaseMedicationName(
      (hasSupportedMetadataName ? metadataMedicationName : '')
      || explicitMention?.name
      || findMedicationNameBesideDose({
        sourceText,
        dose: doseMatch?.[1],
        unit: doseMatch?.[2],
      })
    );
    if (!doseMatch) {
      const missingName=normalizeWhitespace(metadata.medicationName || item.title);
      const quantity=readMedicationDose({dose:metadata.dose||item.dose,unit:metadata.unit||item.unit,medicationName:missingName,sourceText});
      if (missingName && !hasInvalidMedicationName(missingName) && quantity.needsClarification) suggestionsByPlan.set(`clarify:${missingName.toLowerCase()}`,{id:buildSuggestionId(source.id,index,missingName),name:titleCaseMedicationName(missingName),dose:'',unit:'',doseText:'',sourceText,needsReview:true,planAction:'needs_clarification',clarification:{issue:'missing_dose_unit',reason:quantity.reason},reviewReasons:['dose_unit_needed']});
      return;
    }
    if (!name || hasInvalidMedicationName(name) || !doseMatch) return;

    const schedule = normalizeWhitespace(metadata.schedule || metadata.frequency || item.schedule);
    const times = normalizeClockTimes(schedule);
    const dose = doseMatch[1] || '';
    const unit = doseMatch[2] || '';
    const status = normalizeWhitespace(metadata.status || item.status);
    const form = normalizeMedicationForm(metadata.form || item.form || '');
    const canonicalMedicationRecord = {
      schemaVersion: 1,
      recordKind: 'plan',
      action: 'record_current',
      medication: { name, dose, unit, form },
      schedule: { type: 'daily', text: schedule, times },
      effective: { date: '', dateText: '', certainty: '' },
      provider: '',
      reason: '',
      source: { sourceId: normalizeWhitespace(source.id), text: sourceText },
    };
    const suggestion = normalizeBackendSuggestion({
      id: buildSuggestionId(source.id, index, name),
      name,
      dose,
      unit,
      schedule,
      times,
      time: times[0] || '',
      status,
      form,
      notes: normalizeWhitespace(item.content),
      sourceText,
      needsReview: true,
      planAction: 'record_current',
      canonicalMedicationRecord,
    }, index, source);
    const planKey = [name.toLowerCase(), dose, unit.toLowerCase(), schedule.toLowerCase()].join('|');
    if (!suggestionsByPlan.has(planKey)) suggestionsByPlan.set(planKey, suggestion);
  });

  return [...suggestionsByPlan.values()];
};

export const getMedicationSourceSuggestions = (source = {}) => {
  const text = [
    source.cleanedText,
    source.rawText,
    source.originalText,
    source.sourceText,
  ].find((value) => normalizeWhitespace(value));
  const structuredSuggestions = getStructuredMedicationSuggestions(source);
  const backendSuggestions = getBackendMedicationSuggestions(source);
  const canonicalBackendSuggestions = backendSuggestions.filter((suggestion) => (
    suggestion.canonicalMedicationRecord?.schemaVersion === 1
  ));
  const hasIncompleteHistoricalSnapshot = canonicalBackendSuggestions.some((suggestion) => (
    suggestion.planAction === 'historical_snapshot'
    && !suggestion.historicalSnapshot?.medications?.length
  ));
  const hasInvalidBackendNames = backendSuggestions.some((suggestion) => (
    (suggestion.planAction !== 'needs_clarification' && hasInvalidMedicationName(suggestion.name))
    || (
      suggestion.planAction === 'record_current'
      && (() => {
        const evidence = findExplicitMedicationMention(suggestion.sourceText || text);
        return evidence && normalizeWhitespace(evidence.name).toLowerCase()
          !== normalizeWhitespace(suggestion.name).toLowerCase();
      })()
    )
  ));

  // New captures use the backend-owned medication interpretation unchanged.
  // The source-text parsers below are a read adapter for older saved captures.
  if (canonicalBackendSuggestions.length && !hasInvalidBackendNames && !hasIncompleteHistoricalSnapshot) {
    return canonicalBackendSuggestions;
  }

  // Older saved captures can contain correct structured AI fields alongside a
  // malformed deterministic suggestion. Preserve those fields without reparsing prose.
  if (structuredSuggestions.length) {
    return structuredSuggestions;
  }

  // A persisted generic suggestion can predate specialized medication instructions.
  // Explicit source wording is safer than treating that stale suggestion as current.
  const historicalSnapshot = text ? parseHistoricalMedicationSnapshot(text, source) : null;
  if (historicalSnapshot) return [historicalSnapshot];

  // A persisted generic suggestion can predate temporary-hold support. The explicit
  // source instruction is safer than treating that stale suggestion as a new medication.
  const temporaryHoldSuggestion = text ? parseTemporaryHold(text, source) : null;
  if (temporaryHoldSuggestion) return [temporaryHoldSuggestion];
  const discontinuationSuggestion = text ? parseMedicationDiscontinuation(text, source) : null;
  if (discontinuationSuggestion) return [discontinuationSuggestion];
  const completedCourseSuggestion = text ? parseCompletedMedicationCourse(text, source) : null;
  if (completedCourseSuggestion) return [completedCourseSuggestion];

  if (backendSuggestions.length && !hasInvalidBackendNames) {
    return backendSuggestions;
  }

  if (!text) return backendSuggestions.filter((suggestion) => !hasInvalidMedicationName(suggestion.name));

  const medicationPlanSuggestion = parseMedicationPlanDocument(text, source);
  if (medicationPlanSuggestion && !hasInvalidMedicationName(medicationPlanSuggestion.name)) {
    return [medicationPlanSuggestion];
  }

  const pipeTableSuggestions = getPipeTableSuggestionsFromText(text, source);
  if (pipeTableSuggestions.length) {
    return pipeTableSuggestions.filter((suggestion) => !hasInvalidMedicationName(suggestion.name));
  }

  const blockSuggestions = getMedicationBlockSuggestionsFromText(text, source);
  if (blockSuggestions.length) {
    return blockSuggestions.filter((suggestion) => !hasInvalidMedicationName(suggestion.name));
  }

  return getMedicationLineEntries(text)
    .map((line, index) => parseMedicationLine(line, index, source))
    .filter((suggestion) => suggestion && !hasInvalidMedicationName(suggestion.name));
};

export const hasMedicationSourceSuggestions = (source = {}) => (
  getMedicationSourceSuggestions(source).length > 0
);

export {buildMedicationFromSuggestion} from './medicationSuggestionRecord';
